import "./AdvancedTextArea.scss";

import cx from "classnames";
import { NOTES_CHARACTER_LIMIT } from "constants/FormConstants";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Redo,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Underline as UnderlineIcon,
  Undo,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Link } from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import type { FieldErrors } from "react-hook-form";

type ToolbarProps = {
  editor: ReturnType<typeof useEditor>;
  isEditable: boolean;
};

const Toolbar = ({ editor, isEditable }: ToolbarProps) => {
  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor || !isEditable) return null;

  return (
    <div className="AdvancedTextEditor__ToolBar">
      <div className="toolbar-group">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "is-active" : ""}
          title="Bold"
        >
          <Bold size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "is-active" : ""}
          title="Italic"
        >
          <Italic size={12} />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={editor.isActive("underline") ? "is-active" : ""}
          title="Underline"
        >
          <UnderlineIcon size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          className={editor.isActive("strike") ? "is-active" : ""}
          title="Strikethrough"
        >
          <Strikethrough size={12} />
        </button>
      </div>
      <div className="toolbar-group">
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "is-active" : ""}
          title="Bullet List"
        >
          <List size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "is-active" : ""}
          title="Numbered List"
        >
          <ListOrdered size={12} />
        </button>
      </div>
      <div className="toolbar-group">
        <button
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={editor.isActive({ textAlign: "left" }) ? "is-active" : ""}
          title="Align Left"
        >
          <AlignLeft size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={
            editor.isActive({ textAlign: "center" }) ? "is-active" : ""
          }
          title="Align Center"
        >
          <AlignCenter size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={editor.isActive({ textAlign: "right" }) ? "is-active" : ""}
          title="Align Right"
        >
          <AlignRight size={12} />
        </button>
      </div>
      <div className="toolbar-group">
        <button
          onClick={setLink}
          className={editor.isActive("link") ? "is-active" : ""}
          title="Add Link"
        >
          <LinkIcon size={12} />
        </button>
        <button
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          title="Insert Table"
        >
          <TableIcon size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().deleteTable().run()}
          disabled={!editor.can().deleteTable()}
          className={!editor.can().deleteTable() ? "disabled" : ""}
          title="Delete Table"
        >
          <Trash2 size={12} />
        </button>
      </div>
      <div className="toolbar-group">
        <button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo size={12} />
        </button>
        <button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo size={12} />
        </button>
      </div>
    </div>
  );
};

Toolbar.displayName = "Toolbar";

type AdvancedTextAreaProps = {
  value: string;
  name: string;
  onChange: (value: string) => void;
  isEditable: boolean;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  errors?: FieldErrors;
  searchTerm?: string;
};

const AdvancedTextEditor = ({
  value,
  name,
  onChange,
  isEditable,
  placeholder,
  onFocus,
  onBlur,
  errors,
  searchTerm,
}: AdvancedTextAreaProps) => {
  const onChangeRef = useRef(onChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const valueRef = useRef(value);
  const internalUpdateRef = useRef(false);
  const isPastingRef = useRef(false);
  const isFocusedRef = useRef(false);
  const [selectionState, setSelectionState] = useState<string>("");

  // keep refs updated if the props change
  useEffect(() => {
    onChangeRef.current = onChange;
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;
  }, [onChange, onFocus, onBlur]);

  // Update value ref when prop changes
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Process initial content for the editor
  const initialContent = useMemo(() => {
    // Default empty state
    const emptyEditorState = { type: "doc", content: [{ type: "paragraph" }] };

    if (!value) return emptyEditorState;

    try {
      // try to parse the value as JSON
      const parsedContent = JSON.parse(value);
      return parsedContent;
    } catch (e) {
      // if parsing fails, assume it's legacy plain text and convert it to TipTap format
      console.warn(
        "AdvancedTextArea: Converting legacy plain text to TipTap JSON format:",
        value,
      );
      return {
        type: "doc",
        content: [
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: value,
              },
            ],
          },
        ],
      };
    }
  }, [value]);

  const editorConfig = useMemo(
    () => ({
      extensions: [
        StarterKit.configure({
          bulletList: { keepMarks: true },
          orderedList: { keepMarks: true },
          link: false, // disable StarterKit's link extension since we're adding it separately
          underline: false, // disable StarterKit's underline extension since we're adding it separately
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        // autolink converts bare typed/pasted URLs into real links. Safe as
        // long as this content is only ever displayed via EditorContent
        // below (ProseMirror's own safe DOM rendering) -- confirmed there's
        // no dangerouslySetInnerHTML or other raw-HTML render path for any
        // advancedEdit field's stored JSON anywhere in the app (frontend or
        // backend docx/PDF/report generation) as of writing. Re-check this
        // if any advancedEdit field's content (claim_notes included) is
        // ever rendered outside this editor.
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
        }),
        Table.configure({
          resizable: false,
        }),
        TableRow,
        TableHeader,
        TableCell,
        Underline,
      ],
      content: initialContent,
      editable: isEditable,
      editorProps: {
        handlePaste: () => {
          // mark that we're in a paste operation
          isPastingRef.current = true;

          // reset the flag after a short delay to allow the onUpdate to process
          setTimeout(() => {
            isPastingRef.current = false;
          }, 0);

          // return false to let the default paste handling continue
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        // if this is an internal update from prop sync AND we're not pasting, skip it
        if (internalUpdateRef.current && !isPastingRef.current) {
          internalUpdateRef.current = false;
          return;
        }

        // if we were pasting, reset the internal update flag but continue processing
        if (isPastingRef.current && internalUpdateRef.current) {
          internalUpdateRef.current = false;
        }

        const text = editor.getText();
        // if text exceeds max length, prevent the update
        if (text.length > NOTES_CHARACTER_LIMIT) {
          return;
        }

        const json = editor.getJSON();
        const stringified = JSON.stringify(json);
        const isEmpty = editor.isEmpty;

        if (isEmpty) {
          const emptyContent =
            '{"type":"doc","content":[{"type":"paragraph"}]}';
          if (valueRef.current !== emptyContent) {
            onChangeRef.current(emptyContent);
          }
        } else if (valueRef.current !== stringified) {
          onChangeRef.current(stringified);
        }
      },
      onFocus: () => {
        isFocusedRef.current = true;
        if (onFocusRef.current) {
          onFocusRef.current();
        }
      },
      onBlur: () => {
        isFocusedRef.current = false;
        if (onBlurRef.current) {
          onBlurRef.current();
        }
      },
    }),
    [initialContent, isEditable],
  );

  const editor = useEditor(editorConfig);

  // only update editable state if it actually changed
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    if (editor.isEditable !== isEditable) {
      editor.setEditable(isEditable);
    }
  }, [editor, isEditable]);

  // Effect to synchronize external value prop changes *to* the editor
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    // Never clobber in-progress typing with a stale form/server value.
    if (isFocusedRef.current) return;

    // Determine the canonical empty state representation used internally
    const emptyEditorState = '{"type":"doc","content":[{"type":"paragraph"}]}';
    let valueToParse = value;

    // Normalize incoming null/undefined/empty string to the canonical empty state
    if (!valueToParse || valueToParse === '""' || valueToParse === "{}") {
      valueToParse = emptyEditorState;
    }

    try {
      // Attempt to parse and then stringify to ensure consistent format
      const parsedPropValue = JSON.parse(valueToParse);
      const stringifiedPropValue = JSON.stringify(parsedPropValue);

      // Get current editor content safely
      const currentJsonString = JSON.stringify(editor.getJSON());

      // Only update editor content if the incoming prop value (normalized)
      // is different from the current editor content (normalized).
      if (stringifiedPropValue !== currentJsonString) {
        try {
          internalUpdateRef.current = true;
          editor.commands.setContent(parsedPropValue, { emitUpdate: false });
        } catch (e) {
          console.error("Error setting editor content from prop:", e);
          internalUpdateRef.current = true;
          editor.commands.setContent(JSON.parse(emptyEditorState), {
            emitUpdate: false,
          });
        }
      }
    } catch (e) {
      // If parsing fails, assume it's legacy plain text and convert it to TipTap format
      console.warn(
        "AdvancedTextArea: Converting legacy plain text to TipTap JSON format:",
        value,
      );
      const plainTextContent = {
        type: "doc",
        content: [
          {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: valueToParse,
              },
            ],
          },
        ],
      };
      try {
        internalUpdateRef.current = true;
        editor.commands.setContent(plainTextContent, { emitUpdate: false });
      } catch (setContentError) {
        console.error("Error setting plain text content:", setContentError);
      }
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || !searchTerm?.trim()) {
      return;
    }

    const normalizedSearchTerm = searchTerm.toLowerCase();
    let matchRange: { from: number; to: number } | null = null;

    editor.state.doc.descendants((node, position) => {
      if (!node.isText || !node.text || matchRange) {
        return !matchRange;
      }

      const matchIndex = node.text.toLowerCase().indexOf(normalizedSearchTerm);
      if (matchIndex < 0) {
        return true;
      }

      matchRange = {
        from: position + matchIndex,
        to: position + matchIndex + searchTerm.length,
      };

      return false;
    });

    if (!matchRange) {
      return;
    }

    editor.commands.focus();
    editor.commands.setTextSelection(matchRange);
    editor.commands.scrollIntoView();
  }, [editor, searchTerm, value]);

  const handleEditorClick = useCallback((e: React.MouseEvent) => {
    // prevent page refresh when clicking WYSIWYG buttons
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Track selection changes to force toolbar state updates
  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      setSelectionState(JSON.stringify(editor.view.state.selection));
    };

    editor.on("selectionUpdate", handleSelectionUpdate);

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, [editor]);

  // track active states for toolbar buttons to ensure proper re-rendering
  const getToolbarState = useCallback(() => {
    if (!editor) return null;
    return {
      bold: editor.isActive("bold"),
      italic: editor.isActive("italic"),
      underline: editor.isActive("underline"),
      strike: editor.isActive("strike"),
      bulletList: editor.isActive("bulletList"),
      orderedList: editor.isActive("orderedList"),
      alignLeft: editor.isActive({ textAlign: "left" }),
      alignCenter: editor.isActive({ textAlign: "center" }),
      alignRight: editor.isActive({ textAlign: "right" }),
      link: editor.isActive("link"),
      selectionState,
    };
  }, [editor, selectionState]);

  const toolbarState = editor ? getToolbarState() : null;

  const memoizedToolbar = useMemo(() => {
    if (!editor || !isEditable) return null;
    return <Toolbar editor={editor} isEditable={isEditable} />;
  }, [editor, isEditable, toolbarState]);

  return (
    <div
      className={cx("AdvancedTextEditor__Wrapper", {
        "AdvancedTextEditor__Wrapper--disabled": !isEditable,
        "AdvancedTextEditor__Wrapper--error": errors && errors[name],
      })}
      onClick={handleEditorClick}
    >
      {memoizedToolbar}
      <EditorContent
        editor={editor}
        className="AdvancedTextEditor__Editor"
        placeholder={placeholder}
      />
    </div>
  );
};

export default memo(AdvancedTextEditor);
